import {
  createApiRef,
  FetchApi,
  DiscoveryApi,
  IdentityApi,
} from '@backstage/core-plugin-api';

import {
  Prompt,
  Agent,
  Vote,
  Comment,
  PromptSubmission,
  AgentSubmission,
  PromptStatus,
  PromptCategory,
  AgentTool,
} from './types';

export const promptLibraryApiRef = createApiRef<PromptLibraryApi>({
  id: 'plugin.prompt-library.api',
});

export interface PromptLibraryApi {
  // Prompts
  getPrompts(options?: {
    status?: PromptStatus;
    category?: PromptCategory;
    search?: string;
    sort?: 'votes' | 'newest' | 'oldest' | 'views';
  }): Promise<Prompt[]>;
  getPrompt(id: number): Promise<Prompt>;
  submitPrompt(prompt: PromptSubmission): Promise<Prompt>;
  updatePrompt(id: number, updates: Partial<PromptSubmission>): Promise<Prompt>;
  deletePrompt(id: number): Promise<void>;

  // Agents
  getAgents(options?: {
    status?: PromptStatus;
    category?: PromptCategory;
    tool?: AgentTool;
    search?: string;
    sort?: 'votes' | 'newest' | 'oldest' | 'views';
  }): Promise<Agent[]>;
  getAgent(id: number): Promise<Agent>;
  submitAgent(agent: AgentSubmission): Promise<Agent>;
  updateAgent(id: number, updates: Partial<AgentSubmission>): Promise<Agent>;
  deleteAgent(id: number): Promise<void>;

  // Votes
  getUserVotes(): Promise<Vote[]>;
  castVote(
    itemId: number,
    itemType: 'prompt' | 'agent',
    voteType: 'up' | 'down',
  ): Promise<{ action: string }>;

  // Comments
  getComments(itemId: number, itemType: 'prompt' | 'agent'): Promise<Comment[]>;
  addComment(
    itemId: number,
    itemType: 'prompt' | 'agent',
    itemTitle: string,
    text: string,
  ): Promise<Comment>;
  deleteComment(id: number): Promise<void>;

  // Admin
  isAdmin(): Promise<boolean>;
  getPendingItems(): Promise<{ prompts: Prompt[]; agents: Agent[] }>;
  approveItem(type: 'prompt' | 'agent', id: number): Promise<void>;
  rejectItem(type: 'prompt' | 'agent', id: number): Promise<void>;
  seedData(
    type?: 'prompts' | 'agents' | 'both',
  ): Promise<{ seeded: { prompts: number; agents: number } }>;

  // Views
  incrementViewCount(
    itemType: 'prompt' | 'agent',
    id: number,
  ): Promise<{ view_count: number }>;
}

export class PromptLibraryClient implements PromptLibraryApi {
  private readonly fetchApi: FetchApi;
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;

  constructor(options: {
    fetchApi: FetchApi;
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
  }) {
    this.fetchApi = options.fetchApi;
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }

  private async getBaseUrl() {
    return await this.discoveryApi.getBaseUrl('prompt-library');
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const { token } = await this.identityApi.getCredentials();
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  private async fetch<T>(path: string, options?: RequestInit): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const headers = await this.getAuthHeaders();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...options,
      headers: { ...headers, ...options?.headers },
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Request failed with status ${response.status}`,
      );
    }

    return response.json();
  }

  // ============ PROMPTS ============

  async getPrompts(options?: {
    status?: PromptStatus;
    category?: PromptCategory;
    search?: string;
    sort?: 'votes' | 'newest' | 'oldest' | 'views';
  }): Promise<Prompt[]> {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.category) params.append('category', options.category);
    if (options?.search) params.append('search', options.search);
    if (options?.sort) params.append('sort', options.sort);
    const queryString = params.toString();
    return this.fetch(`/prompts${queryString ? `?${queryString}` : ''}`);
  }

  async getPrompt(id: number): Promise<Prompt> {
    return this.fetch(`/prompts/${id}`);
  }

  async submitPrompt(prompt: PromptSubmission): Promise<Prompt> {
    return this.fetch('/prompts', {
      method: 'POST',
      body: JSON.stringify(prompt),
    });
  }

  async updatePrompt(
    id: number,
    updates: Partial<PromptSubmission>,
  ): Promise<Prompt> {
    return this.fetch(`/prompts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deletePrompt(id: number): Promise<void> {
    await this.fetch(`/prompts/${id}`, { method: 'DELETE' });
  }

  // ============ AGENTS ============

  async getAgents(options?: {
    status?: PromptStatus;
    category?: PromptCategory;
    tool?: AgentTool;
    search?: string;
    sort?: 'votes' | 'newest' | 'oldest' | 'views';
  }): Promise<Agent[]> {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.category) params.append('category', options.category);
    if (options?.tool) params.append('tool', options.tool);
    if (options?.search) params.append('search', options.search);
    if (options?.sort) params.append('sort', options.sort);
    const queryString = params.toString();
    return this.fetch(`/agents${queryString ? `?${queryString}` : ''}`);
  }

  async getAgent(id: number): Promise<Agent> {
    return this.fetch(`/agents/${id}`);
  }

  async submitAgent(agent: AgentSubmission): Promise<Agent> {
    return this.fetch('/agents', {
      method: 'POST',
      body: JSON.stringify(agent),
    });
  }

  async updateAgent(
    id: number,
    updates: Partial<AgentSubmission>,
  ): Promise<Agent> {
    return this.fetch(`/agents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteAgent(id: number): Promise<void> {
    await this.fetch(`/agents/${id}`, { method: 'DELETE' });
  }

  // ============ VOTES ============

  async getUserVotes(): Promise<Vote[]> {
    return this.fetch('/votes');
  }

  async castVote(
    itemId: number,
    itemType: 'prompt' | 'agent',
    voteType: 'up' | 'down',
  ): Promise<{ action: string }> {
    return this.fetch('/votes', {
      method: 'POST',
      body: JSON.stringify({
        item_id: itemId,
        item_type: itemType,
        vote_type: voteType,
      }),
    });
  }

  // ============ COMMENTS ============

  async getComments(
    itemId: number,
    itemType: 'prompt' | 'agent',
  ): Promise<Comment[]> {
    return this.fetch(`/comments?itemId=${itemId}&itemType=${itemType}`);
  }

  async addComment(
    itemId: number,
    itemType: 'prompt' | 'agent',
    itemTitle: string,
    text: string,
  ): Promise<Comment> {
    return this.fetch('/comments', {
      method: 'POST',
      body: JSON.stringify({
        item_id: itemId,
        item_type: itemType,
        item_title: itemTitle,
        comment_text: text,
      }),
    });
  }

  async deleteComment(id: number): Promise<void> {
    await this.fetch(`/comments/${id}`, { method: 'DELETE' });
  }

  // ============ ADMIN ============

  async isAdmin(): Promise<boolean> {
    try {
      const result = await this.fetch<{ isAdmin: boolean }>('/admin/is-admin');
      return result.isAdmin;
    } catch {
      return false;
    }
  }

  async getPendingItems(): Promise<{ prompts: Prompt[]; agents: Agent[] }> {
    return this.fetch('/admin/pending');
  }

  async approveItem(type: 'prompt' | 'agent', id: number): Promise<void> {
    await this.fetch(`/admin/approve/${type}/${id}`, { method: 'POST' });
  }

  async rejectItem(type: 'prompt' | 'agent', id: number): Promise<void> {
    await this.fetch(`/admin/reject/${type}/${id}`, { method: 'POST' });
  }

  async seedData(
    type?: 'prompts' | 'agents' | 'both',
  ): Promise<{ seeded: { prompts: number; agents: number } }> {
    const params = type ? `?type=${type}` : '';
    return this.fetch(`/admin/seed${params}`, { method: 'POST' });
  }

  // ============ VIEWS ============

  async incrementViewCount(
    itemType: 'prompt' | 'agent',
    id: number,
  ): Promise<{ view_count: number }> {
    return this.fetch(`/views/${itemType}/${id}`, { method: 'POST' });
  }
}
